import {
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';

import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  pipe,
  switchMap,
  tap,
} from 'rxjs';

import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Optional,
  ChangeDetectionStrategy,
} from '@angular/core';

import { ImagesContainer } from '@ui/orders/graphql';
import { select, Store } from '@ngrx/store';
import {
  selectFilesInBasket,
  selectPhotosList,
  selectPrintsInBasket,
} from '@ui/products/shopping-cart-back/actions/selector';

import { WallDecorPieceForDisplayType } from '@ui/look-book-front/wall-decor-front/graphql';
import { setCurrentOrganization } from 'ZalamoLibrary/src/lib/core/organizations/organization/redux/organization.actions';
import { IS_PHOTOGRAPHER } from 'ZalamoLibrary/src/lib/zalamo/user/ViewerType';
import { WallDecorRenderPreviewConfig } from '@ui/products/products-front/components/prints-full-editor/graphql';
import { GetPublicSessionDetailsGQL } from '../../../../../../../../public-session/src/graphql';

@Component({
  selector: 'lib-public-session-wall-decor-editor',
  templateUrl: './public-session-wall-decor-editor.component.html',
  styleUrls: ['./public-session-wall-decor-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PublicSessionWallDecorEditorComponent implements OnInit {
  publicSessionId$ = new BehaviorSubject(null);
  context = ImagesContainer.PublicSession;
  filesInBasket$ = this.store.pipe(
    select(selectFilesInBasket),
    map((v) => v.filter(({ isFull }) => isFull).map(({ photoId }) => photoId)),
  );
  @Input() isSinglePhotoMode = false;
  @Input() photoRatio = 1;
  printsInBasket$ = this.store.pipe(
    select(selectPrintsInBasket),
    map((v) => v.map(({ photoId }) => photoId)),
  );
  availableImages$ = combineLatest([
    this.filesInBasket$,
    this.printsInBasket$,
  ]).pipe(map(([files, prints]) => [...new Set([...files, ...prints])]));
  photoGroupHasPhoto$ = this.store.pipe(select(selectPhotosList));
  _publicSessionId: string;
  @Input() preset: WallDecorRenderPreviewConfig;

  get publicSessionId() {
    return this._publicSessionId;
  }

  @Input() type: WallDecorPieceForDisplayType =
    WallDecorPieceForDisplayType.Canvas;
  @Input() isUpdate = false;
  @Input() currentLookBookConfigId = null;
  closed = new EventEmitter();

  publicSessionDetails$ = this.publicSessionId$.pipe(
    switchMap((publicSessionId) =>
      this.publicSessionDetails
        .watch({
          ...{
            fetchPolicy: this.isPhotographer ? 'network-only' : 'cache-first',
          },
          variables: { publicSessionId },
        })
        .valueChanges.pipe(
          throwOnQueryError,
          filter(hasCompleteQueryData),
          map((result) => result.data['getPublicSessionForDisplay']),
        ),
    ),
    tap(({ organizationId }) =>
      this.store.dispatch(setCurrentOrganization({ organizationId })),
    ),
    tap((x) => console.log('GET PUBLIC SESSION DETAILS')),
  );
  rounding$ = this.publicSessionDetails$.pipe(
    map(({ lookBookConfig: { wallDecorConfig, tambourConfig } }) =>
      this.type === WallDecorPieceForDisplayType.Canvas
        ? wallDecorConfig.rounding
        : tambourConfig.rounding,
    ),
  );
  limitPersonalization$ = this.publicSessionDetails$.pipe(
    map(
      ({
        lookBookConfig: {
          wallDecorConfig: { disablePersonalizationOnSinglePhoto },
        },
      }) => disablePersonalizationOnSinglePhoto && this.isSinglePhotoMode,
    ),
  );

  preConfig$ = this.publicSessionDetails$.pipe(
    map(({ lookBookConfig: { wallDecorConfig, tambourConfig } }) =>
      this.type === WallDecorPieceForDisplayType.Canvas
        ? wallDecorConfig.preConfig
        : tambourConfig.preConfig,
    ),
  );

  @Input() set publicSessionId(publicSessionId: string) {
    this._publicSessionId = publicSessionId;
    this.publicSessionId$.next(publicSessionId);
  }

  constructor(
    private publicSessionDetails: GetPublicSessionDetailsGQL,
    private store: Store,
    // private publicSessionsGraphService: PublicSessionsGraphService,
    @Optional() @Inject(IS_PHOTOGRAPHER) public isPhotographer: boolean,
  ) {}

  ngOnInit(): void {}
}
